/* eslint-disable react/prop-types */ // TODO: add props
import { Fragment, useEffect } from 'react';
import { scroller } from 'react-scroll';
import cx from 'classnames';
import { Table } from 'edrolo/domains/teacherResultsTable';
import { flatten, find } from 'lodash';
import SolutionCell from '../SolutionCell';
import style from './ExamTable.scss';
import ExamGridResultSquare from '../ExamGridResultSquare';
import StudentNameCell from './StudentNameCell';
import { EXAM_QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';
import MultiChoiceQuestionAverageCell from 'edrolo/components/exams/grid/MultiChoiceQuestionAverageCell';
import StudentTotalAverageCell from 'edrolo/components/exams/grid/StudentTotalAverageCell';
import QuestionAverageCell from './QuestionAverageCell';
import ClassAveragesChart from 'edrolo/components/exams/charts/ClassAveragesChart';

const SpacerCell = () => (
  <Table.ColHeader className={cx(style.spacerCell, style.bottomBorder)} />
);

const answerable = (question) => Number(question.marks) > 0;

const SectionHeaders = ({ sections }) => (
  <Table.Row>
    {sections.map((section, idx) => (
      <Fragment key={`section-idx-${idx}`}>
        {idx === 0 ? (
          <>
            <Table.ColHeader
              key="blank-1"
              isSticky
              isHorizSticky
              className={style.sectionHeadingBlank}
            />
            <Table.ColHeader
              key="blank-2"
              isSticky
              className={style.sectionHeadingBlank}
            />
          </>
        ) : (
          <Table.ColHeader
            key={`section-spacer-idx=${idx}`}
            className={cx(style.spacerCell, style.sectionHeadingBlank)}
            isSticky
          />
        )}
        <Table.ColHeader
          className={style.sectionHeading}
          colSpan={section.flatQuestions.filter(answerable).length}
          key={`section-${section.id}`}
          isSticky
        >
          {section.title || `Section ${section.serial}`}
        </Table.ColHeader>
      </Fragment>
    ))}
  </Table.Row>
);

const QuestionHeaders = ({ markedQuestionsBySection }) => (
  <Table.Row>
    {markedQuestionsBySection.map((section, idx) => (
      <Fragment key={`section-idx-${idx}`}>
        {idx === 0 ? (
          <>
            <Table.ColHeader
              key="blank-1"
              className={style.stickyRowTwo}
              isSticky
              isHorizSticky
            />
            <Table.ColHeader
              key="blank-2"
              className={style.stickyRowTwo}
              isSticky
            />
          </>
        ) : (
          <Table.ColHeader
            key={`section-spacer-idx=${idx}`}
            className={cx(style.spacerCell, style.stickyRowTwo)}
            stickyRowTwo
          />
        )}
        {section.map((question) => (
          <Table.ColHeader
            key={`question-${question.id}`}
            className={cx(style.stickyRowTwo, style.questionHeading)}
          >
            Q{question.fullSerial}
          </Table.ColHeader>
        ))}
      </Fragment>
    ))}
  </Table.Row>
);

const SolutionHeaders = ({
  markedQuestionsBySection,
  solutionData,
  analyticsPayload,
}) => {
  return (
    <Table.Row>
      {markedQuestionsBySection.map((questions, idx) => (
        <Fragment key={`section-idx-${idx}`}>
          {idx === 0 ? (
            <Table.ColHeader key="blank" isHorizSticky colSpan={2} />
          ) : (
            <Table.ColHeader
              key={`section-spacer-idx=${idx}`}
              className={style.spacerCell}
            />
          )}
          {questions.map((question) => {
            const solution = find(solutionData.questions, { id: question.id });
            return (
              <Table.ColHeader
                key={`question-${question.id}`}
                className={style.solutionHeading}
              >
                <SolutionCell
                  question={question}
                  solution={solution}
                  origin="exam_grid"
                  analyticsPayload={analyticsPayload}
                />
              </Table.ColHeader>
            );
          })}
        </Fragment>
      ))}
    </Table.Row>
  );
};

const calcVCAAAverage = (question) =>
  question.vcaa_average ? `${Math.round(question.vcaa_average)}%` : 'N/A';

const VCAAAverageRow = ({ markedQuestionsBySection }) => {
  return (
    <Table.Row>
      {markedQuestionsBySection.map((questions, idx) => (
        <Fragment key={`section-idx-${idx}`}>
          {idx === 0 ? (
            <>
              <Table.RowHeader key="name" isSticky className={style.rowHeading}>
                VCAA Average
              </Table.RowHeader>
              <SpacerCell />
            </>
          ) : (
            <Table.Cell
              key={`section-spacer-idx=${idx}`}
              className={style.spacerCell}
            />
          )}
          {questions.map((question) => (
            <Table.Cell
              key={`question-${question.id}`}
              className={style.VCAAAverageCell}
            >
              {calcVCAAAverage(question)}
            </Table.Cell>
          ))}
        </Fragment>
      ))}
    </Table.Row>
  );
};

const ClassAverageRow = ({
  markedQuestionsBySection,
  classAveragesBySection,
  sittingData,
}) => (
  <Table.Row>
    {markedQuestionsBySection.map((questions, idx) => (
      <Fragment key={`section-idx-${idx}`}>
        {idx === 0 ? (
          <>
            <Table.RowHeader key="name" isSticky className={style.rowHeading}>
              Class Average
            </Table.RowHeader>
            <SpacerCell />
          </>
        ) : (
          <Table.Cell
            key={`section-spacer-idx=${idx}`}
            className={style.spacerCell}
          />
        )}

        {questions.map((question) => {
          const average = find(flatten(classAveragesBySection), {
            question: question.id,
          });

          return question.question_type ===
            EXAM_QUESTION_TYPES.SINGLE_ANSWER_MULTI_CHOICE ? (
            <Table.Cell key={`question-${question.id}`}>
              <MultiChoiceQuestionAverageCell
                key={question.id}
                question={question}
                average={average.average}
              />
            </Table.Cell>
          ) : (
            <Table.Cell key={`question-${question.id}`}>
              <QuestionAverageCell
                key={question.id}
                question={question}
                average={average.average}
                sittingData={sittingData}
              />
            </Table.Cell>
          );
        })}
      </Fragment>
    ))}
  </Table.Row>
);

const StudentResultRow = ({
  student,
  sitting,
  markedQuestionsBySection,
  totalMarksAttempted,
  totalMarksObtained,
  handleChangedResponse,
  onChangedResponse,
  deleteExamSitting,
  examData,
  classAveragePercentageForThisExam,
  analyticsPayload,
  shouldHighlight = false,
}) => {
  useEffect(() => {
    if (shouldHighlight) {
      // Calculate an offset of 10% of the viewport height to position the
      // scroller slightly below the top of the screen.
      const offset = window.innerHeight * 0.1;
      scroller.scrollTo(student.id, {
        duration: 1000,
        smooth: true,
        offset: -offset,
      });
    }
  });

  return (
    <Table.Row
      id={`${student.id}`}
      className={cx({ [style.highlightedRow]: shouldHighlight })}
    >
      {markedQuestionsBySection.map((questions, idx) => (
        <Fragment key={`section-idx-${idx}`}>
          {idx === 0 ? (
            <>
              <StudentNameCell
                sitting={sitting}
                student={student}
                deleteExamSitting={deleteExamSitting}
                className={style.rowHeading}
              />
              <StudentTotalAverageCell
                displayTotal={!!sitting}
                classAverage={classAveragePercentageForThisExam}
                totalMarksAvailable={totalMarksAttempted}
                totalMarksObtained={totalMarksObtained}
                className={style.bottomBorder}
              />
            </>
          ) : (
            <Table.Cell
              key={`section-spacer-idx-${idx}`}
              className={style.spacerCell}
            />
          )}

          {examData.is_submittable && (!sitting || !sitting.submitted_ts) ? (
            <Table.Cell colSpan={questions.length}>
              {idx === 0 ? 'Unsubmitted' : ''}
            </Table.Cell>
          ) : (
            questions.map((question) => {
              return (
                <Table.Cell key={`question-${question.id}`}>
                  <ExamGridResultSquare
                    sittingId={sitting?.id}
                    student={student}
                    question={question}
                    response={
                      sitting &&
                      find(sitting.responses, {
                        question: question.id,
                      })
                    }
                    onChangedResponse={handleChangedResponse}
                    sittingLoaded
                    analyticsPayload={analyticsPayload}
                  />
                </Table.Cell>
              );
            })
          )}
        </Fragment>
      ))}
    </Table.Row>
  );
};

const ClassAveragesChartRow = ({
  examData,
  classAveragesBySection,
  markedQuestionsForExam,
}) => {
  if (examData.curriculum !== 'vce') {
    return null;
  }
  return (
    <Table.Row>
      <Fragment>
        <Table.RowHeader key="blank" isSticky />
        <SpacerCell />
        <Table.Cell
          colSpan={
            markedQuestionsForExam.length + classAveragesBySection.length - 1
          }
        >
          <ClassAveragesChart
            classAverages={flatten(classAveragesBySection)}
            markedQuestions={markedQuestionsForExam}
          />
        </Table.Cell>
      </Fragment>
    </Table.Row>
  );
};

const ExamTable = ({
  examData,
  markedQuestionsBySection,
  markedQuestionsForExam,
  classAveragesBySection,
  totalMarksAttemptedPerSitting,
  totalMarksObtainedPerSitting,
  solutionData,
  sittingData,
  sortedStudents,
  isAnonymized,
  handleChangedResponse,
  deleteExamSitting,
  analyticsPayload,
  highlightProfileId,
}) => {
  const showVCAA = examData.is_submittable && examData.curriculum === 'vce';
  return (
    <Table>
      <Table.Head>
        <SectionHeaders sections={examData.sections} />
        <QuestionHeaders markedQuestionsBySection={markedQuestionsBySection} />
        <SolutionHeaders
          markedQuestionsBySection={markedQuestionsBySection}
          solutionData={solutionData}
          analyticsPayload={analyticsPayload}
        />
      </Table.Head>
      <Table.Body>
        {showVCAA ? (
          <VCAAAverageRow
            key="vcaa-average"
            markedQuestionsBySection={markedQuestionsBySection}
          />
        ) : null}
        <ClassAverageRow
          key="class-average"
          markedQuestionsBySection={markedQuestionsBySection}
          classAveragesBySection={classAveragesBySection}
          sittingData={sittingData}
          Z
        />
        {sortedStudents.map((student) => {
          const sitting = find(sittingData, { profile: student.id });
          return (
            <StudentResultRow
              student={student}
              sitting={sitting}
              markedQuestionsBySection={markedQuestionsBySection}
              handleChangedResponse={handleChangedResponse}
              totalMarksAttempted={
                sitting && totalMarksAttemptedPerSitting.get(sitting.id)
              }
              totalMarksObtained={
                sitting && totalMarksObtainedPerSitting.get(sitting.id)
              }
              deleteExamSitting={deleteExamSitting}
              examData={examData}
              key={`student-${student.id}`}
              analyticsPayload={analyticsPayload}
              shouldHighlight={highlightProfileId === student.id}
            />
          );
        })}
        <ClassAveragesChartRow
          examData={examData}
          markedQuestionsForExam={markedQuestionsForExam}
          classAveragesBySection={classAveragesBySection}
        />
      </Table.Body>
    </Table>
  );
};

export default ExamTable;
